const BiopharmaceuticsUnit4Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <div>
        <h2 class="text-2xl font-black text-blue-900 mb-3 border-b-2 border-slate-200 pb-2">Unit IV: Multicompartment Models & Multiple Dosing</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            In reality, the body does not behave as a single homogenous unit. This unit introduces **Multicompartment Models** to describe drugs that distribute at varying rates into different tissues, alongside the kinetics of **Multiple Dosing Regimens** used in clinical settings.
        </p>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-4 border-b border-slate-100 pb-2">1. Two-Compartment Open Model</h3>
        <p class="text-[13px] text-slate-700 mb-4">
            When a drug is injected, it distributes rapidly into highly perfused organs, but takes longer to enter poorly perfused tissues. Therefore, the body is resolved mathematically into two distinct compartments:
        </p>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div class="bg-blue-50 p-4 border border-blue-200 rounded-lg">
                <h4 class="font-bold text-blue-900 text-[14px] mb-1">A. Central Compartment (Compartment 1)</h4>
                <p class="text-[12px] text-slate-600 mb-2">Consists of blood plasma, extracellular fluid, and **highly perfused organs** that equilibrate with the drug instantly.</p>
                <p class="text-[12px] text-slate-700 font-medium"><em>Examples:</em> Heart, Lungs, Liver, Kidneys, and Brain.</p>
            </div>
            <div class="bg-blue-50 p-4 border border-blue-200 rounded-lg">
                <h4 class="font-bold text-blue-900 text-[14px] mb-1">B. Peripheral Compartment (Compartment 2)</h4>
                <p class="text-[12px] text-slate-600 mb-2">Consists of tissues that are **poorly perfused**, where drug equilibration occurs slowly across tissue membranes.</p>
                <p class="text-[12px] text-slate-700 font-medium"><em>Examples:</em> Skeletal Muscles, Skin, Adipose (Fat) tissue, and Bone.</p>
            </div>
        </div>

        <div class="mb-4">
            <h4 class="text-[16px] font-bold text-blue-900 mb-2">IV Bolus Administration & Plasma Concentration Curve</h4>
            <p class="text-[13px] text-slate-700 mb-3">
                When a drug is given as an IV bolus in a two-compartment model, the semi-log plot of plasma concentration versus time shows a **biphasic curve** (two distinct slopes):
            </p>
            
            <ol class="list-decimal pl-5 space-y-3 text-[13px] text-slate-700 mb-4">
                <li>
                    <strong>Distribution Phase (&alpha;-phase):</strong> The initial steep decline in plasma concentration. The drug is moving rapidly from the central compartment into the peripheral tissues.
                </li>
                <li>
                    <strong>Post-Distributive Elimination Phase (&beta;-phase):</strong> The linear, shallower decline that follows. Distribution equilibrium has been reached, and the decline is strictly due to drug elimination from the body.
                </li>
            </ol>

            <div class="bg-slate-50 p-4 rounded border border-slate-200 text-[13px] text-slate-700">
                <p class="font-bold text-blue-900 mb-2 text-center">Biexponential Disposition Equation:</p>
                <div class="bg-white p-2 rounded border border-slate-200 text-center font-mono text-[14px] font-bold text-blue-800 mb-2">
                    C = A·e<sup>-&alpha;t</sup> + B·e<sup>-&beta;t</sup>
                </div>
                <ul class="list-disc pl-5 text-[12px] text-slate-600 space-y-1">
                    <li><strong>C:</strong> Plasma concentration at any time <em>t</em>.</li>
                    <li><strong>&alpha; (Alpha):</strong> Hybrid first-order rate constant for the distribution phase.</li>
                    <li><strong>&beta; (Beta):</strong> Hybrid first-order rate constant for the elimination phase ($\alpha > \beta$).</li>
                    <li><strong>A & B:</strong> Extrapolated zero-time plasma concentration intercepts for the distribution and elimination phases, respectively.</li>
                </ul>
            </div>
        </div>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-4 border-b border-slate-100 pb-2">2. Kinetics of Multiple Dosing</h3>
        <p class="text-[13px] text-slate-700 mb-3 text-justify">
            Single doses are rarely sufficient for treating chronic conditions. To maintain a prolonged therapeutic effect, drugs are administered repeatedly at fixed time intervals ($\tau$ - Tau). 
        </p>

        <div class="bg-indigo-50 border-l-4 border-indigo-600 p-4 rounded-r-xl shadow-sm mb-5">
            <h4 class="text-[15px] font-black text-indigo-900 mb-1">The Concept of Steady-State ($C_{ss}$)</h4>
            <p class="text-[13px] text-slate-700 leading-relaxed text-justify">
                When a drug is given repeatedly, it accumulates in the body because some drug from the previous dose remains when the next dose is given. Eventually, a **Steady-State** is reached where the **Rate of Drug Input equals the Rate of Drug Output**. 
                <br><span class="font-semibold text-indigo-950">Rule of Thumb:</span> It takes approximately **4 to 5 biological half-lives ($t_{1/2}$)** to reach steady state, regardless of the dose or dosing interval.
            </p>
        </div>

        <h4 class="text-[16px] font-bold text-blue-900 mb-2">Fluctuations at Steady-State</h4>
        <p class="text-[13px] text-slate-700 mb-3">At steady state, plasma concentration oscillates between a maximum and a minimum level within every dosing interval:</p>
        
        <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-600 mb-5">
            <li><strong>C<sub>max,ss</sub> (Maximum Steady-State Concentration):</strong> Occurs immediately after a dose is administered at steady state. Must remain below the *Minimum Toxic Concentration (MTC)*.</li>
            <li><strong>C<sub>min,ss</sub> (Minimum Steady-State Concentration):</strong> Occurs at the very end of the dosing interval, just before the next dose. Must remain above the *Minimum Effective Concentration (MEC)* to avoid therapeutic failure.</li>
            <li><strong>Average Steady-State Concentration (C<sub>av,ss</sub>):</strong> The mean concentration during a dosing interval at steady state. Formula: <span class="font-mono bg-slate-100 px-1 rounded text-blue-950">C<sub>av,ss</sub> = [F × Dose] / [Cl<sub>T</sub> × &tau;]</span>.</li>
        </ul>

        <div class="bg-slate-50 p-4 rounded border border-slate-200 text-[13px] text-slate-700">
            <h5 class="font-bold text-blue-900 mb-1">Accumulation Index (R)</h5>
            <p class="text-[12px] text-slate-600">Measures the extent of drug accumulation in the body. It is dependent strictly on the elimination half-life and the dosing interval ($\tau$):</p>
            <div class="font-mono text-center font-bold text-blue-800 my-1 bg-white p-2 rounded border border-slate-100 max-w-xs mx-auto">
                R = 1 / (1 - e<sup>-K<sub>E</sub>&tau;</sup>)
            </div>
        </div>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-4 border-b border-slate-100 pb-2">3. Loading Dose and Maintenance Dose</h3>
        <p class="text-[13px] text-slate-700 mb-4">
            For drugs with long half-lives, waiting 4-5 half-lives to reach the therapeutic steady-state concentration is clinically dangerous (e.g., in serious infections or cardiac emergencies). To solve this, a specialized dosing strategy is used:
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div class="border border-slate-200 p-5 rounded-xl bg-slate-50 shadow-sm">
                <h4 class="text-[16px] font-black text-blue-700 mb-2">A. Loading Dose (D<sub>L</sub>)</h4>
                <p class="text-[13px] text-slate-700 mb-3 text-justify">
                    A single large dose administered at the beginning of therapy to **instantly achieve the target therapeutic plasma concentration** without waiting for accumulation over time.
                </p>
                <div class="bg-white p-3 rounded border border-slate-200 text-[12px] font-mono text-blue-900 mb-2">
                    <strong>Formula:</strong> <br>
                    D<sub>L</sub> = C<sub>ss</sub> × V<sub>d</sub> <br>
                    or <br>
                    D<sub>L</sub> = D<sub>M</sub> × R = D<sub>M</sub> / (1 - e<sup>-K<sub>E</sub>&tau;</sup>)
                </div>
                <p class="text-[11px] text-slate-500 italic">Clinical Use: Giving IV loading dose of Lidocaine during cardiac arrhythmia, or an initial high dose of an antibiotic.</p>
            </div>

            <div class="border border-slate-200 p-5 rounded-xl bg-slate-50 shadow-sm">
                <h4 class="text-[16px] font-black text-blue-700 mb-2">B. Maintenance Dose (D<sub>M</sub>)</h4>
                <p class="text-[13px] text-slate-700 mb-3 text-justify">
                    Smaller, repeated doses administered at regular intervals ($\tau$) to **maintain the steady-state therapeutic level** by exactly replacing the amount of drug that was eliminated since the last dose.
                </p>
                <div class="bg-white p-3 rounded border border-slate-200 text-[12px] font-mono text-blue-900 mb-2">
                    <strong>Formula:</strong> <br>
                    D<sub>M</sub> = C<sub>ss</sub> × Cl<sub>T</sub> × &tau; <br>
                    <span class="text-slate-500 font-sans block mt-1">If the drug is given orally, bioavailbility (F) must be considered:</span>
                    D<sub>M</sub> = [C<sub>ss</sub> × Cl<sub>T</sub> × &tau;] / F
                </div>
                <p class="text-[11px] text-slate-500 italic">Clinical Significance: Avoids subtherapeutic drops and prevents toxicity by keeping drug levels perfectly within the safe therapeutic window.</p>
            </div>
        </div>
    </div>

</div>
`;

export default BiopharmaceuticsUnit4Notes;